import React, { useContext, useEffect, useState } from 'react';
import {
  Box, Grid, Typography, Button, Stack, Divider,
  Card, CardActionArea, CardContent, IconButton, Dialog, Skeleton,
} from '@mui/material';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import NotificationsRoundedIcon    from '@mui/icons-material/NotificationsRounded';
import axios from 'axios';
import { useGrowSetup } from '../../GrowSetupContext';
import { AuthContext }  from '../../components/signin/AuthContext';
import AlertConfigCard  from '../../components/AlertConfigCard';
import AlertMainGrid    from '../../components/AlertConfigMainGrid';

export default function AlertConfigPage() {
  const { grow_setup } = useGrowSetup();
  const { token }      = useContext(AuthContext);
  const [alerts,   setAlerts]  = useState([]);
  const [loading,  setLoading] = useState(true);
  const [openNew,  setOpenNew] = useState(false);

  const fetchAlerts = async () => {
    if (!grow_setup) return;
    setLoading(true);
    try {
      const res = await axios.post(`/get_alert_config/${grow_setup}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlerts(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAlerts(); }, [grow_setup]);

  if (loading) return (
    <Grid container spacing={2}>
      {[0,1,2].map(i => (
        <Grid item xs={12} sm={6} md={4} key={i}>
          <Skeleton variant="rounded" height={180} sx={{ bgcolor: 'rgba(255,255,255,0.04)', borderRadius: '16px' }} />
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box>
      {/* Header row */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="overline" sx={{ color: '#76c442', fontWeight: 700, letterSpacing: '0.1em', fontSize: '0.7rem' }}>
            Benachrichtigungen
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.25 }}>
            {alerts.length} {alerts.length === 1 ? 'Regel' : 'Regeln'} konfiguriert
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineRoundedIcon />}
          onClick={() => setOpenNew(true)}
          sx={{ borderRadius: '50px', px: 3 }}
        >
          Hinzufügen
        </Button>
      </Stack>

      <Divider sx={{ mb: 3, borderColor: 'rgba(118,196,66,0.1)' }} />

      <Grid container spacing={2}>
        {alerts.map((cfg, i) => (
          <Grid item xs={12} sm={6} md={4} key={cfg.id ?? i}>
            <Box sx={{
              animation: `fadeSlideUpCard 0.5s cubic-bezier(0.22,1,0.36,1) ${i * 60}ms both`,
              '& .MuiCard-root': {
                background: 'rgba(15,28,15,0.75) !important',
                border: '1px solid rgba(118,196,66,0.12) !important',
                backdropFilter: 'blur(16px)',
                borderRadius: '14px !important',
                height: '100%',
              },
            }}>
              <AlertConfigCard {...cfg} grow_setup={grow_setup} token={token} />
            </Box>
          </Grid>
        ))}

        {/* Add new card */}
        <Grid item xs={12} sm={6} md={4}>
          <Box sx={{ animation: `fadeSlideUpCard 0.5s cubic-bezier(0.22,1,0.36,1) ${alerts.length * 60}ms both` }}>
            <Card sx={{
              background: 'rgba(15,28,15,0.4) !important',
              border: '1px dashed rgba(118,196,66,0.25) !important',
              borderRadius: '14px !important',
              minHeight: 140,
            }}>
              <CardActionArea
                sx={{ height: '100%', minHeight: 140, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 3 }}
                onClick={() => setOpenNew(true)}
              >
                <IconButton sx={{ color: '#76c442', mb: 1 }}>
                  <AddCircleOutlineRoundedIcon sx={{ fontSize: 36 }} />
                </IconButton>
                <CardContent sx={{ pt: 0 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
                    Neue Benachrichtigung
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {alerts.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <NotificationsRoundedIcon sx={{ fontSize: 56, color: 'rgba(118,196,66,0.2)', mb: 2 }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.4)' }}>
            Noch keine Benachrichtigungen. Füge die erste hinzu!
          </Typography>
        </Box>
      )}

      {/* Dialog: reuse existing AlertMainGrid dialog pattern */}
      <Dialog
        open={openNew}
        onClose={() => setOpenNew(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(8,20,9,0.97)',
            border: '1px solid rgba(118,196,66,0.15)',
            borderRadius: '18px',
            backdropFilter: 'blur(24px)',
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          <AlertMainGrid />
        </Box>
      </Dialog>

      <style>{`
        @keyframes fadeSlideUpCard {
          from { opacity:0; transform:translateY(20px); }
          to   { opacity:1; transform:translateY(0);    }
        }
      `}</style>
    </Box>
  );
}
